import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Bot, Send, Sparkles, ShieldAlert, Lightbulb, History, Loader2,
  ThumbsUp, ThumbsDown, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { aiAssistantApi, type AIFeedbackValue } from "@/lib/ai-assistant-api";
import { formatTimeIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { ApiError } from "@/lib/api";

interface Msg {
  role: "user" | "assistant";
  content: string;
  time: string;
  interactionId?: string;
  feedback?: AIFeedbackValue;
  error?: boolean;
}

const SUGGESTIONS = [
  "How many employees missed check-in today?",
  "Summarize today's attendance by department",
  "How many pending correction requests are there?",
  "Which sites have the most exceptions today?",
  "How do I configure a geofence?",
];


/** Escape HTML, then render **bold** — the answer is model output, not trusted markup. */
function renderAnswer(text: string): string {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
}

const Assistant = () => {
  const tz = useTenantTimezone();
  // Message stamps follow tenant time like every other timestamp in the app.
  const now = () => formatTimeIn(new Date(), tz);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [sending, setSending] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [sessionId] = useState(() =>
    typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `s-${Date.now()}`,
  );
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // The caller's own recent questions (works even when the provider is disabled).
  useEffect(() => {
    aiAssistantApi
      .history({ pageSize: 5 })
      .then((page) => setRecent(page.data.map((h) => h.question)))
      .catch(() => undefined);
  }, []);

  const send = async (text?: string) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", content: q, time: now() }]);
    setInput("");
    setSending(true);
    try {
      const res = await aiAssistantApi.ask({ persona: "ADMIN", message: q, sessionId });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.answer, time: now(), interactionId: res.interactionId },
      ]);
    } catch (e) {
      let content = "Something went wrong. Please try again.";
      if (e instanceof ApiError && e.status === 503) {
        setDisabled(true);
        content = "The AI assistant isn't enabled on this workspace yet.";
      } else if (e instanceof ApiError && e.status === 429) {
        content = e.message || "You've reached today's assistant limit. Try again tomorrow.";
      } else if (e instanceof ApiError && e.message) {
        content = e.message;
      }
      setMessages((m) => [...m, { role: "assistant", content, time: now(), error: true }]);
    } finally {
      setSending(false);
    }
  };

  const rate = async (idx: number, value: AIFeedbackValue) => {
    const msg = messages[idx];
    if (!msg?.interactionId || msg.feedback) return;
    // Optimistic; revert on failure.
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, feedback: value } : x)));
    try {
      await aiAssistantApi.feedback(msg.interactionId, value);
      toast.success("Thanks for the feedback");
    } catch {
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, feedback: undefined } : x)));
      toast.error("Could not save feedback");
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
      <Card className="flex flex-col h-[calc(100vh-10rem)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 border-b p-4 bg-gradient-to-r from-primary/5 to-info/5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary text-white shadow-glow">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-semibold leading-tight">GeoKerja Assistant</div>
            <div className="text-xs text-muted-foreground">Read-only · scoped to your access · audited</div>
          </div>
          <div className={`ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium ${disabled ? "bg-muted text-muted-foreground" : "bg-success/10 text-success"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${disabled ? "bg-muted-foreground" : "animate-pulse-soft bg-success"}`} />
            {disabled ? "Unavailable" : "Online"}
          </div>
        </div>

        {/* Conversation */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center px-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-primary text-white shadow-glow">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">How can I help you today?</h3>
              <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
                Ask about today's attendance, exceptions, pending requests, or sites. I answer from
                aggregate figures within your access — I can't name individual employees.
              </p>
              <div className="mt-6 grid w-full max-w-xl gap-2 sm:grid-cols-2">
                {SUGGESTIONS.slice(0, 4).map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={sending} className="rounded-lg border bg-card p-3 text-left text-sm hover:border-primary/30 hover:bg-secondary/50 transition-all disabled:opacity-50">
                    <Lightbulb className="mb-1.5 h-3.5 w-3.5 text-primary" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white ${m.error ? "bg-warning" : "bg-gradient-primary"}`}>
                    {m.error ? <AlertTriangle className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                )}
                <div className={`max-w-[75%] ${m.role === "user" ? "" : "space-y-1"}`}>
                  <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-gradient-primary text-white" : m.error ? "bg-warning/10 text-foreground" : "bg-secondary"}`}>
                    {m.role === "assistant" ? (
                      <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderAnswer(m.content) }} />
                    ) : (
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    )}
                    <div className={`mt-1 text-[10px] ${m.role === "user" ? "text-white/70" : "text-muted-foreground"}`}>{m.time}</div>
                  </div>
                  {m.role === "assistant" && !m.error && m.interactionId && (
                    <div className="flex items-center gap-1 pl-1">
                      {m.feedback ? (
                        <span className="text-[10px] text-muted-foreground">Feedback noted · thank you</span>
                      ) : (
                        <>
                          <button onClick={() => rate(i, "HELPFUL")} title="Helpful" className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-success">
                            <ThumbsUp className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => rate(i, "NOT_HELPFUL")} title="Not helpful" className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => rate(i, "INCORRECT")} title="Incorrect" className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-destructive">
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold">SM</div>
                )}
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-white">
                <Bot className="h-4 w-4" />
              </div>
              <div className="flex items-center gap-2 rounded-2xl bg-secondary px-4 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="border-t p-3 bg-card">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={disabled ? "Assistant unavailable" : "Ask GeoKerja Assistant…"}
              className="h-10"
              disabled={sending || disabled}
            />
            <Button type="submit" size="icon" className="h-10 w-10 bg-gradient-primary" disabled={sending || disabled || !input.trim()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
          <div className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5">
            <ShieldAlert className="h-3 w-3" />
            Answers use aggregate data within your access and are logged for audit. The assistant can't take actions.
          </div>
        </div>
      </Card>

      {/* Sidebar */}
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold text-sm">Suggested prompts</h3>
          </div>
          <div className="space-y-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} disabled={sending || disabled} className="block w-full rounded-md border bg-secondary/30 p-2 text-left text-xs hover:bg-secondary hover:border-primary/30 transition-all disabled:opacity-50">
                {s}
              </button>
            ))}
          </div>
        </Card>

        {recent.length > 0 && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-primary" />
              <h3 className="font-display font-semibold text-sm">Your recent queries</h3>
            </div>
            <ul className="space-y-2 text-xs text-muted-foreground">
              {recent.map((r, i) => (
                <li key={i} className="cursor-pointer hover:text-foreground line-clamp-2" onClick={() => send(r)}>· {r}</li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="p-4 bg-gradient-to-br from-primary/5 to-info/5 border-primary/20">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">Tip</div>
          <p className="text-sm leading-relaxed">
            Ask about totals and summaries — “how many missed check-in at each site today?” For
            named details, open the Attendance monitor or Requests list.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default Assistant;
